import type { WorkScheduleDayModel } from "./type"
import type { WorkSchedule, WorkScheduleDay } from "@/types/offices"
import _toNumber from "lodash/toSafeInteger"
import { WEEK_DAYS } from "@/const"
import { MINUTES_PER_DAY } from "@/utils/calendar"
import dayjs from "@/utils/dayjs"

/** Why a time range doesn't fit the office work schedule. */
export type WorkScheduleViolation = "closed_day" | "outside_hours"

interface MinutesInterval {
	from: number
	to: number
}

// True when at least one day of the week has some time slot configured
export function hasWorkSchedule(workSchedule: Partial<WorkSchedule>): boolean {
	return Object.values(workSchedule).some(slots => !!slots?.length)
}

// Minutes elapsed since midnight for a "HH:mm" time ("18.30" / "18,30" are tolerated), null when unparsable
function parseTimeToMinutes(time?: string | null): number | null {
	if (!time) return null

	const [h, m = "0"] = time.replace(/[.,]/g, ":").split(":")
	const hours = Number(h)
	const minutes = Number(m)

	if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null

	return hours * 60 + minutes
}

// Opening intervals (in minutes since midnight) of the weekday `date` falls on, merged and sorted
function getDayIntervals(workSchedule: Partial<WorkSchedule>, date: dayjs.Dayjs): MinutesInterval[] {
	// dayjs weekdays start on Sunday, WEEK_DAYS on Monday
	const weekDay = WEEK_DAYS[(date.day() + 6) % 7]!

	const intervals = (workSchedule[weekDay] ?? [])
		.map(slot => {
			const from = parseTimeToMinutes(slot.from)
			const to = parseTimeToMinutes(slot.to)

			if (from === null || to === null) return null

			// A closing time that isn't after the opening one (e.g. "18:00"–"00:00") runs until midnight
			return { from, to: to > from ? to : MINUTES_PER_DAY }
		})
		.filter((interval): interval is MinutesInterval => interval !== null)
		.sort((a, b) => a.from - b.from)

	// Merge overlapping/adjacent slots so a range crossing e.g. 09:00–13:00 + 13:00–18:00 still fits
	return intervals.reduce<MinutesInterval[]>((merged, interval) => {
		const last = merged[merged.length - 1]

		if (last && interval.from <= last.to) {
			last.to = Math.max(last.to, interval.to)
		} else {
			merged.push({ ...interval })
		}

		return merged
	}, [])
}

/**
 * Checks a `[startMs, endMs)` range against the office work schedule.
 * Returns null when the range fits (or the office has no schedule configured),
 * "closed_day" when every day it touches is closed, "outside_hours" otherwise.
 */
export function getWorkScheduleViolation(
	workSchedule: Partial<WorkSchedule>,
	startMs: number,
	endMs: number
): WorkScheduleViolation | null {
	if (!hasWorkSchedule(workSchedule)) return null

	// A zero-length range is still checked, as an instant sitting on the start day
	const end = Math.max(endMs, startMs + 1)

	let cursor = dayjs(startMs).startOf("day")
	let outside = false
	let touchesOpenDay = false

	while (cursor.valueOf() < end) {
		const dayStart = cursor.valueOf()
		const nextDay = cursor.add(1, "day")
		const segmentStart = Math.max(startMs, dayStart)
		const segmentEnd = Math.min(end, nextDay.valueOf())

		if (segmentEnd > segmentStart) {
			const intervals = getDayIntervals(workSchedule, cursor)

			if (!intervals.length) {
				outside = true
			} else {
				touchesOpenDay = true

				const from = dayjs(segmentStart).diff(cursor, "minute")
				const to = dayjs(segmentEnd).diff(cursor, "minute")

				if (!intervals.some(interval => from >= interval.from && to <= interval.to)) {
					outside = true
				}
			}
		}

		cursor = nextDay
	}

	if (!outside) return null

	return touchesOpenDay ? "outside_hours" : "closed_day"
}

// Converts a time string ("09:25", "18.40", etc.) to a float (e.g., 9.42), or 0 if invalid
function parseTimeToNumber(time?: string | null): number {
	if (!time) return 0
	// Replace . with : for uniformity
	const normalized = time.replace(".", ":").replace(",", ":")
	const [h, m] = normalized.split(":")
	const hours = _toNumber(h)
	const minutes = _toNumber(m || "0")

	return dayjs().set("h", hours).set("m", minutes).valueOf()
}

// Converts a WorkScheduleDay to WorkScheduleDayModel
export function workScheduleDayToModel(schedule: WorkScheduleDay): WorkScheduleDayModel {
	return {
		from: parseTimeToNumber(schedule.from),
		to: parseTimeToNumber(schedule.to)
	}
}

// Converts a WorkScheduleDayModel to WorkScheduleDay
export function workScheduleDayFromModel(schedule: WorkScheduleDayModel): WorkScheduleDay {
	return {
		from: dayjs(schedule.from).format("HH:mm"),
		to: dayjs(schedule.to).format("HH:mm")
	}
}
